import React, { useMemo, useState } from 'react';
import SearchableSelect from './SearchableSelect';

const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;
const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  'X-CSRF-Token': csrfToken(),
});

const EMPTY = { provider_id: '', geo: '', value: '', priority: 1 };

const PRIORITY_BADGE = {
  1: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  2: 'bg-sky-50 text-sky-700 border-sky-200',
  3: 'bg-amber-50 text-amber-700 border-amber-200',
  4: 'bg-orange-50 text-orange-700 border-orange-200',
  5: 'bg-rose-50 text-rose-700 border-rose-200',
};

const card = 'bg-white rounded-xl border border-slate-200/70 shadow-sm';
const fieldLabel = 'block text-xs font-medium text-slate-500 uppercase mb-1.5';
const textInput =
  'w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const ghostButton =
  'inline-flex items-center px-3 py-1.5 text-sm font-medium text-slate-700 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors shadow-sm';

const StatCard = ({ label, value, iconBg, iconColor, children }) => (
  <div className={`${card} p-6`}>
    <div className='flex items-center'>
      <div className={`flex-shrink-0 p-3 rounded-lg ${iconBg}`}>
        <svg className={`w-6 h-6 ${iconColor}`} viewBox='0 0 24 24' fill='none' stroke='currentColor'>
          {children}
        </svg>
      </div>
      <div className='ml-5'>
        <div className='text-sm font-medium text-slate-600'>{label}</div>
        <div className='mt-1 text-2xl font-semibold text-slate-900'>{value}</div>
      </div>
    </div>
  </div>
);

const SenderIdsManager = ({
  initialSenderIds,
  geos,
  providers,
  priorities,
  initialProviderFilter,
}) => {
  const [items, setItems] = useState(initialSenderIds || []);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [providerFilter, setProviderFilter] = useState(initialProviderFilter || '');
  const [geoFilter, setGeoFilter] = useState('');
  const [recentGeo, setRecentGeo] = useState(null);
  const [recentId, setRecentId] = useState(null);

  const priorityOptions = (priorities || [1, 2, 3, 4, 5]).map((p) => ({
    value: p,
    label: p === 1 ? '1 — highest' : String(p),
  }));

  const groups = useMemo(() => {
    const filtered = items.filter(
      (i) =>
        (!providerFilter || i.provider_id === providerFilter) &&
        (!geoFilter || i.geo === geoFilter)
    );

    const byGeo = {};
    filtered.forEach((i) => {
      if (!byGeo[i.geo]) byGeo[i.geo] = [];
      byGeo[i.geo].push(i);
    });

    return Object.keys(byGeo)
      .sort((a, b) => {
        if (a === recentGeo) return -1;
        if (b === recentGeo) return 1;
        return a.localeCompare(b);
      })
      .map((geo) => {
        const rows = byGeo[geo]
          .slice()
          .sort((a, b) => a.priority - b.priority || a.id - b.id);
        const routable = rows.filter((r) => r.provider_active !== false);
        const topPriority = routable.length ? routable[0].priority : null;
        const primaryCount = routable.filter((r) => r.priority === topPriority).length;
        const backups = routable.length - primaryCount;
        const disabled = rows.length - routable.length;
        return { geo, rows, topPriority, primaryCount, backups, disabled };
      });
  }, [items, providerFilter, geoFilter, recentGeo]);

  const backupCount = items.filter((i) => {
    if (i.provider_active === false) return false;

    const sameGeo = items.filter((x) => x.geo === i.geo && x.provider_active !== false);
    return i.priority > Math.min(...sameGeo.map((x) => x.priority));
  }).length;

  const save = async (payload, id) => {
    const url = id ? `/sender_ids/${id}` : '/sender_ids';
    const method = id ? 'PATCH' : 'POST';
    const res = await fetch(url, {
      method,
      headers: jsonHeaders(),
      body: JSON.stringify({ sender_id: payload }),
    });
    const data = await res.json();
    if (!res.ok) {
      setError((data.errors || ['Save failed']).join(', '));
      return null;
    }
    setError('');
    return data;
  };

  const handleCreate = async () => {
    if (!form.provider_id || !form.geo || !form.value.trim()) {
      setError('Provider, GEO and Sender ID are required.');
      return;
    }
    const created = await save(form, null);
    if (created) {
      setItems((prev) => [created, ...prev]);
      setRecentGeo(created.geo);
      setRecentId(created.id);
      if (geoFilter && geoFilter !== created.geo) setGeoFilter('');
      if (providerFilter && providerFilter !== created.provider_id) setProviderFilter('');
      setForm({ ...EMPTY, provider_id: form.provider_id, priority: form.priority });
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      provider_id: item.provider_id,
      geo: item.geo,
      value: item.value,
      priority: item.priority,
    });
  };

  const handleUpdate = async () => {
    const updated = await save(editForm, editingId);
    if (updated) {
      setItems((prev) => prev.map((i) => (i.id === editingId ? updated : i)));
      setEditingId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete Sender ID "${item.value}" (${item.geo})?`)) return;
    await fetch(`/sender_ids/${item.id}`, { method: 'DELETE', headers: jsonHeaders() });
    setItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  const roleLabel = (row, group) => {
    if (row.provider_active === false) return 'Not routed';
    if (row.priority !== group.topPriority) return 'Backup';
    return group.primaryCount > 1
      ? `Primary · ${Math.round(100 / group.primaryCount)}%`
      : 'Primary';
  };

  return (
    <div className='min-h-screen bg-slate-50/50'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='flex items-center justify-between mb-8'>
          <div>
            <h1 className='text-2xl font-semibold text-slate-900'>Sender ID Routing</h1>
            <p className='mt-1 text-sm text-slate-600'>
              Traffic goes through the highest priority (1) binding for the country. Equal
              priorities share it evenly. Lower priorities are backups, used automatically
              only when the primary gateway returns an error.
            </p>
          </div>

          <a href='/senders' className={`${ghostButton} whitespace-nowrap`}>
            <svg className='w-4 h-4 mr-1.5' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
            Senders
          </a>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <StatCard
            label='Total Bindings'
            value={items.length}
            iconBg='bg-blue-50'
            iconColor='text-blue-600'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M4 6h16M4 10h10M4 14h16M4 18h10'
            />
          </StatCard>
          <StatCard
            label='GEOs Covered'
            value={new Set(items.filter((i) => i.provider_active !== false).map((i) => i.geo)).size}
            iconBg='bg-emerald-50'
            iconColor='text-emerald-600'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18a15 15 0 010-18z'
            />
          </StatCard>
          <StatCard
            label='Backup Bindings'
            value={backupCount}
            iconBg='bg-violet-50'
            iconColor='text-violet-600'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.5 0L3.2 16.25A2 2 0 005 19z'
            />
          </StatCard>
        </div>

        <div className={`${card} p-6 mb-6`}>
          <h2 className='text-sm font-semibold text-slate-900 mb-4'>Add binding</h2>
          <div className='grid grid-cols-1 md:grid-cols-5 gap-4 items-end'>
            <div>
              <label className={fieldLabel}>Provider</label>
              <SearchableSelect
                options={providers}
                value={form.provider_id}
                onChange={(v) => setForm({ ...form, provider_id: v || '' })}
                placeholder='Select gateway…'
                isClearable
              />
            </div>
            <div>
              <label className={fieldLabel}>GEO / Country</label>
              <SearchableSelect
                options={geos}
                value={form.geo}
                onChange={(v) => setForm({ ...form, geo: v || '' })}
                placeholder='Select GEO…'
                isClearable
              />
            </div>
            <div>
              <label className={fieldLabel}>Sender ID</label>
              <input
                className={textInput}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder='e.g. PariPesa'
              />
            </div>
            <div>
              <label className={fieldLabel}>Priority</label>
              <SearchableSelect
                options={priorityOptions}
                value={form.priority}
                onChange={(v) => setForm({ ...form, priority: v || 1 })}
                isSearchable={false}
              />
            </div>
            <button
              onClick={handleCreate}
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
            >
              <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                />
              </svg>
              Add binding
            </button>
          </div>
          {error && <p className='mt-3 text-sm text-red-600'>{error}</p>}
        </div>

        <div className={`${card} overflow-hidden`}>
          <div className='flex flex-wrap items-end gap-4 px-6 py-4 border-b border-slate-200'>
            <div className='w-64'>
              <label className={fieldLabel}>Filter by provider</label>
              <SearchableSelect
                options={providers}
                value={providerFilter}
                onChange={(v) => setProviderFilter(v || '')}
                placeholder='All providers'
                isClearable
              />
            </div>
            <div className='w-64'>
              <label className={fieldLabel}>Filter by GEO</label>
              <SearchableSelect
                options={geos}
                value={geoFilter}
                onChange={(v) => setGeoFilter(v || '')}
                placeholder='All GEOs'
                isClearable
              />
            </div>
            {(providerFilter || geoFilter) && (
              <button
                onClick={() => {
                  setProviderFilter('');
                  setGeoFilter('');
                }}
                className={ghostButton}
              >
                Clear filters
              </button>
            )}
            <div className='ml-auto text-sm text-slate-500'>
              {groups.reduce((sum, g) => sum + g.rows.length, 0)} of {items.length} bindings
            </div>
          </div>

          <div className='min-w-full divide-y divide-slate-200'>
            <div className='bg-slate-50/50'>
              <div className='grid grid-cols-12 px-6 py-3'>
                <div className='col-span-3'>
                  <span className='text-xs font-medium text-slate-500 uppercase'>Provider</span>
                </div>
                <div className='col-span-3'>
                  <span className='text-xs font-medium text-slate-500 uppercase'>Sender ID</span>
                </div>
                <div className='col-span-2'>
                  <span className='text-xs font-medium text-slate-500 uppercase'>Priority</span>
                </div>
                <div className='col-span-2'>
                  <span className='text-xs font-medium text-slate-500 uppercase'>Role</span>
                </div>
                <div className='col-span-2 text-right'>
                  <span className='text-xs font-medium text-slate-500 uppercase'>Actions</span>
                </div>
              </div>
            </div>

            <div className='divide-y divide-slate-200 bg-white'>
              {groups.map((group) => (
                <div key={group.geo}>
                  <div className='flex items-center justify-between px-6 py-2.5 bg-slate-50/50 border-b border-slate-200'>
                    <span className='text-sm font-semibold text-slate-900'>{group.geo}</span>
                    <span className='text-xs text-slate-500'>
                      {group.primaryCount === 0
                        ? 'no active binding'
                        : group.primaryCount > 1
                        ? `${group.primaryCount} primary bindings split evenly`
                        : '1 primary binding'}
                      {group.backups > 0 && ` · ${group.backups} backup`}
                      {group.disabled > 0 && ` · ${group.disabled} not routed`}
                    </span>
                  </div>

                  <div className='divide-y divide-slate-200'>
                    {group.rows.map((item) =>
                      editingId === item.id ? (
                        <div id={`sender-id-${item.id}`} key={item.id} className='px-6 py-4 bg-blue-50/30'>
                          <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-3'>
                            <div>
                              <label className={fieldLabel}>Provider</label>
                              <SearchableSelect
                                options={providers}
                                value={editForm.provider_id}
                                onChange={(v) =>
                                  setEditForm({ ...editForm, provider_id: v || '' })
                                }
                              />
                            </div>
                            <div>
                              <label className={fieldLabel}>GEO / Country</label>
                              <SearchableSelect
                                options={geos}
                                value={editForm.geo}
                                onChange={(v) => setEditForm({ ...editForm, geo: v || '' })}
                              />
                            </div>
                            <div>
                              <label className={fieldLabel}>Sender ID</label>
                              <input
                                className={textInput}
                                value={editForm.value}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, value: e.target.value })
                                }
                              />
                            </div>
                            <div>
                              <label className={fieldLabel}>Priority</label>
                              <SearchableSelect
                                options={priorityOptions}
                                value={editForm.priority}
                                onChange={(v) => setEditForm({ ...editForm, priority: v || 1 })}
                                isSearchable={false}
                              />
                            </div>
                          </div>
                          <div className='flex justify-end gap-2'>
                            <button
                              onClick={() => setEditingId(null)}
                              className={ghostButton}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdate}
                              className='inline-flex items-center rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          key={item.id}
                          id={`sender-id-${item.id}`}
                          className={`grid grid-cols-12 px-6 py-4 transition-colors ${
                            item.id === recentId
                              ? 'bg-emerald-50/60 hover:bg-emerald-50'
                              : 'hover:bg-slate-50/50'
                          }`}
                        >
                          <div className='col-span-3 flex items-center'>
                            <div className='flex-shrink-0 h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center'>
                              <svg
                                className='w-5 h-5 text-slate-600'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth='2'
                                  d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                                />
                              </svg>
                            </div>
                            <div className='ml-4'>
                              <div className='text-sm font-medium text-slate-900 flex items-center gap-2'>
                                {item.provider_name}
                                {item.provider_active === false && (
                                  <span
                                    className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200'
                                    title='Provider is disabled — this binding is skipped, traffic goes to the next priority'
                                  >
                                    disabled
                                  </span>
                                )}
                              </div>
                              <div className='text-sm text-slate-500'>ID: {item.provider_id}</div>
                            </div>
                          </div>

                          <div className='col-span-3 flex items-center'>
                            <span className='text-sm font-medium text-slate-900'>{item.value}</span>
                          </div>

                          <div className='col-span-2 flex items-center'>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded border ${
                                PRIORITY_BADGE[item.priority] || PRIORITY_BADGE[5]
                              }`}
                            >
                              {item.priority}
                            </span>
                          </div>

                          <div className='col-span-2 flex items-center'>
                            <span className='text-sm text-slate-500'>
                              {roleLabel(item, group)}
                            </span>
                          </div>

                          <div className='col-span-2 flex items-center justify-end gap-2'>
                            <button onClick={() => startEdit(item)} className={ghostButton}>
                              <svg
                                className='w-4 h-4 mr-1.5'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth='2'
                                  d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                                />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-white rounded-lg border border-slate-200 hover:bg-red-50 transition-colors shadow-sm'
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))}

              {groups.length === 0 && (
                <div className='px-6 py-12 text-center'>
                  <svg
                    className='mx-auto h-12 w-12 text-slate-400'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M4 6h16M4 10h10M4 14h16M4 18h10'
                    />
                  </svg>
                  <h3 className='mt-4 text-sm font-medium text-slate-900'>
                    No Sender ID bindings found
                  </h3>
                  <p className='mt-1 text-sm text-slate-500'>
                    {items.length
                      ? 'Nothing matches the current filters.'
                      : 'Add a provider + GEO + Sender ID binding to start routing traffic.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SenderIdsManager;
